// src/layouts/AppLayout.tsx
import { Suspense } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, CalendarDays, Dumbbell, Bell as BellNavIcon, User, LogOut } from "lucide-react";
import BackButton from "../components/BackButton";
import Bell from "../pages/components/Bell";
import { logout } from "../services/authService";
import FullScreenLoader from "../components/ui/FullScreenLoader";
import { useAuth } from "../context/AuthProvider";
import { useApp } from "../context/AppContext";
import PushRegistrar from "../components/PushRegistrar";
import { useNotifications } from "../hooks/useNotifications";

const link = ({ isActive }: { isActive: boolean }) =>
  "rounded-lg px-3 py-2 text-sm " +
  (isActive ? "bg-pastel-pink text-slate-900 font-medium" : "hover:bg-pastel-pink/30 text-slate-900");

const MOBILE_TABS = [
  { to: "/app", end: true, label: "Inicio", Icon: Home },
  { to: "/app/classes", end: false, label: "Clases", Icon: CalendarDays },
  { to: "/app/routines", end: false, label: "Rutinas", Icon: Dumbbell },
  { to: "/app/notifications", end: false, label: "Avisos", Icon: BellNavIcon },
  { to: "/app/profile", end: false, label: "Perfil", Icon: User },
];

export default function AppLayout() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const app = useApp();
  const { unread } = useNotifications();

  async function onLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  const tenantId = (app?.tenantId as string) || "";
  const uid = user?.uid || "";

  return (
    <div className="min-h-screen bg-pastel-cream text-slate-900 body-safe">
      {/* Registrador invisible de push */}
      {tenantId && uid ? <PushRegistrar tenantId={tenantId} uid={uid} /> : null}

      {/* Contenedor responsivo */}
      <div className="mx-auto w-full max-w-7xl px-3 sm:px-4 lg:px-6 py-4 grid grid-cols-12 gap-6">
        {/* Sidebar solo desktop */}
        <aside className="hidden md:block md:col-span-3 lg:col-span-2">
          <nav className="flex md:flex-col gap-2">
            <NavLink to="/app" end className={link}>
              Inicio
            </NavLink>
            <NavLink to="/app/classes" className={link}>
              Clases
            </NavLink>
            <NavLink to="/app/routines" className={link}>
              Rutinas
            </NavLink>
            <NavLink to="/app/profile" className={link}>
              Perfil
            </NavLink>
            <NavLink to="/app/notifications" className={link}>
              Notificaciones
            </NavLink>

            <button
              onClick={onLogout}
              className="mt-4 text-left rounded-lg px-3 py-2 text-sm hover:bg-pastel-pink/30 text-slate-900"
            >
              Cerrar sesión
            </button>
          </nav>
        </aside>

        {/* Contenido */}
        <main className="col-span-12 md:col-span-9 lg:col-span-10 pb-24 md:pb-4">
          {/* Header local de página: Back + Bell (logout solo en desktop, en mobile va en la bottom bar) */}
          <div className="flex items-center justify-between mb-3">
            <BackButton fallback="/app" />
            <div className="flex items-center gap-2">
              <Bell />
              <button className="hidden md:inline-flex btn btn-ghost btn-sm" onClick={onLogout}>
                Cerrar sesión
              </button>
            </div>
          </div>

          {/* Loader durante carga perezosa o límites de Suspense internos */}
          <Suspense fallback={<FullScreenLoader message="Cargando sección…" solidBackdrop={false} />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      {/* Bottom bar móvil */}
      <nav
        className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-slate-200 md:hidden shadow-[0_-2px_12px_rgba(0,0,0,0.04)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="mx-auto w-full max-w-lg px-1 py-1.5 grid grid-cols-6 gap-0.5">
          {MOBILE_TABS.map(({ to, end, label, Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `relative flex flex-col items-center justify-center gap-0.5 py-1.5 rounded-xl transition ${
                  isActive ? "text-teal-700" : "text-slate-500 hover:text-slate-700"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className="relative">
                    <Icon className={`w-5 h-5 ${isActive ? "stroke-[2.5]" : ""}`} />
                    {label === "Avisos" && unread > 0 && (
                      <span className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 inline-flex items-center justify-center rounded-full text-[9px] font-bold bg-rose-600 text-white ring-2 ring-white">
                        {unread > 9 ? "9+" : unread}
                      </span>
                    )}
                  </span>
                  <span className="text-[10px] font-medium leading-none">{label}</span>
                </>
              )}
            </NavLink>
          ))}
          {/* Logout rápido en móvil */}
          <button
            onClick={onLogout}
            className="flex flex-col items-center justify-center gap-0.5 py-1.5 rounded-xl text-slate-500 hover:text-rose-600 transition"
            aria-label="Cerrar sesión"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-[10px] font-medium leading-none">Salir</span>
          </button>
        </div>
      </nav>
    </div>
  );
}
